import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { categories, articles } from "@/data/articles";
import { hubs } from "@/data/hubs";
import { Search, ArrowRight } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WebSiteJsonLd, OrganizationJsonLd, FAQJsonLd, BreadcrumbJsonLd } from "@/components/JsonLdSchema";
import { optimizedImage, optimizedSrcSet } from "@/utils/imageOptimization";
import { getDefaultAuthor } from "@/data/authors";
import EditorialDivider from "@/components/EditorialDivider";

const homepageFAQ = [
  {
    question: "What is Life Basics Guide?",
    answer: "Life Basics Guide is a free, ad-free reference site offering clear, practical guidance for adults on everyday life skills—from home repairs and budgeting to cooking, career building, and navigating bureaucracy. Every article is written in plain language with no judgment."
  },
  {
    question: "What topics does Life Basics Guide cover?",
    answer: "We cover eight core areas: Home & Repairs, Money & Finance, Food & Cooking, Cleaning & Routines, Life Admin, Society & Systems, Social & Relationships, and Work & Career. Each category has in-depth hub guides and dozens of supporting articles."
  },
  {
    question: "Is Life Basics Guide free to use?",
    answer: "Yes, completely. There are no courses, subscriptions, quizzes, or upsells. Every article is freely accessible with no signup required. The site is also entirely ad-free."
  },
  {
    question: "How should I use this site?",
    answer: "There's no start or finish—it's a reference. Browse by category or hub if you want to explore a topic in depth, or use the search bar if you have a specific question. Each article focuses on one topic with steps you can follow immediately."
  }
];

// Subtle category accent palette (HSL strings) — dampened, magazine-friendly
const CATEGORY_ACCENTS: Record<string, string> = {
  "home-repairs": "20 35% 45%",          // terra
  "money-finance": "150 25% 35%",        // oliv
  "food-cooking": "15 50% 50%",          // clay
  "cleaning-routines": "190 30% 45%",    // mist
  "life-admin": "220 25% 35%",           // ink-blue
  "society-systems": "260 20% 40%",      // plum
  "social-relationships": "350 30% 50%", // dusty rose
  "work-career": "40 35% 40%",           // bronze
  "housing-renting": "100 20% 38%",      // moss
  "transport-driving": "210 30% 40%",    // steel
  "insurance-protection": "180 25% 35%", // teal
  "digital-life": "240 25% 45%",         // indigo
};

const Index = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const editor = getDefaultAuthor();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const popularArticles = useMemo(() => {
    return [...articles]
      .filter(a => a.featuredImage && a.description)
      .sort((a, b) => (b.datePublished || "").localeCompare(a.datePublished || ""))
      .slice(0, 8);
  }, []);

  const featuredHub = hubs[0];
  const supportingHubs = hubs.slice(1, 5);

  const categoryArticleLinks = useMemo(() => {
    const map: Record<string, { slug: string; title: string }[]> = {};
    categories.forEach(cat => {
      const catArticles = cat.featuredArticles
        ? articles
            .filter(a => cat.featuredArticles!.includes(a.slug))
            .slice(0, 3)
        : articles
            .filter(a => a.categorySlug === cat.slug)
            .slice(0, 3);
      map[cat.slug] = catArticles.map(a => ({ slug: a.slug, title: a.title }));
    });
    return map;
  }, []);

  return (
    <Layout
      title="Life Skills You Were Never Taught"
      description="Clear, practical guidance for adults. No judgment, no fluff—just the information you need to handle everyday tasks with confidence."
      path="/"
    >
      <WebSiteJsonLd />
      <OrganizationJsonLd />
      <FAQJsonLd items={homepageFAQ} />
      <BreadcrumbJsonLd items={[]} />

      {/* Hero — Magazine cover */}
      <section aria-label="Introduction" className="pt-20 md:pt-28 pb-20 md:pb-24">
        <div className="container max-w-4xl text-center">
          <p className="kicker-gold mb-7 justify-center">
            Vol. II · No. 06 — A reference for modern life
          </p>
          <h1 className="display-headline text-foreground mb-8">
            Life skills you<br className="hidden md:block" /> were never taught
          </h1>

          {/* Decorative rule */}
          <div className="flex justify-center my-8">
            <div className="w-16 h-[1px] bg-accent-warm" />
          </div>

          <p className="font-heading italic text-lg md:text-xl text-muted-foreground mb-10 leading-relaxed max-w-2xl mx-auto text-balance">
            Clear, practical guidance for adults — no judgment, no fluff.
            Just the information you need to handle everyday life with quiet confidence.
          </p>

          {/* Minimal search */}
          <form onSubmit={handleSearch} className="max-w-sm mx-auto">
            <div className="relative">
              <Search className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="search"
                placeholder="Search articles..."
                aria-label="Search articles"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-transparent border-b border-border focus:border-foreground transition-colors pl-7 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none"
              />
            </div>
          </form>
        </div>
      </section>

      <EditorialDivider numeral="I" />

      {/* Featured story — 1 large + 2 small */}
      <section aria-label="Featured guides" className="py-16 section-raised">
        <div className="container">
          <div className="flex items-baseline justify-between mb-10">
            <div>
              <p className="kicker-gold mb-3">§ I — The cover story</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
                Start here
              </h2>
            </div>
            <Link to="/categories" className="hidden md:inline text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors">
              All guides →
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Featured hub — large */}
            {featuredHub && (
              <Link
                to={`/hub/${featuredHub.slug}`}
                className="lg:col-span-7 group block"
              >
                <div className="relative aspect-[4/3] md:aspect-[16/10] rounded-sm mb-6 img-hover-soft image-hairline image-vignette">
                  <img
                    src={optimizedImage(featuredHub.featuredImage, 1000) || featuredHub.featuredImage}
                    srcSet={optimizedSrcSet(featuredHub.featuredImage, 1000)}
                    sizes="(min-width: 1024px) 60vw, 100vw"
                    alt={featuredHub.title}
                    className="w-full h-full object-cover"
                    loading="eager"
                    decoding="async"
                    width={1000}
                    height={625}
                  />
                </div>
                <span className="kicker-gold">{featuredHub.categoryName} · Featured guide</span>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground mt-4 mb-3 leading-tight group-hover:text-accent-warm transition-colors text-balance">
                  {featuredHub.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed max-w-2xl">
                  {featuredHub.description}
                </p>
                <span className="text-xs text-foreground font-medium mt-5 inline-flex items-center gap-1.5 uppercase tracking-wider">
                  Read the guide <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            )}

            {/* Supporting hubs — stacked */}
            <div className="lg:col-span-5 flex flex-col divide-y divide-border">
              {supportingHubs.map((hub, i) => (
                <Link
                  key={hub.slug}
                  to={`/hub/${hub.slug}`}
                  className="group py-5 first:pt-0 last:pb-0 flex gap-5 items-start"
                >
                  <span aria-hidden className="numeral-display text-2xl pt-1 flex-shrink-0 w-8 opacity-70 group-hover:opacity-100 transition-opacity tabular-figures">
                    {String(i + 2).padStart(2, "0")}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="label-editorial">{hub.categoryName}</span>
                    <h3 className="font-heading font-bold text-foreground text-lg mt-1.5 leading-snug group-hover:text-accent-warm transition-colors text-balance">
                      {hub.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">
                      {hub.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <EditorialDivider numeral="II" />

      {/* From the editor — DARK INK BAND (signature magazine moment) */}
      <section aria-label="From the editor" className="py-24 section-ink">
        <div className="container max-w-3xl relative z-10">
          <p className="kicker-gold mb-8 justify-center text-center w-full" style={{ display: 'flex', justifyContent: 'center' }}>
            § II — From the editor
          </p>
          <div className="flex flex-col md:flex-row gap-10 items-start">
            <div className="flex-shrink-0 mx-auto md:mx-0">
              <div className="w-24 h-24 rounded-full border border-accent-warm/40 flex items-center justify-center font-heading font-bold italic text-2xl text-accent-warm bg-ink-deep/50">
                CM
              </div>
            </div>
            <div className="flex-1">
              <p className="font-heading italic text-xl md:text-2xl leading-snug mb-6 text-balance" style={{ color: "hsl(var(--ink-deep-foreground))" }}>
                "I kept meeting capable, smart adults who felt quietly ashamed about
                not knowing things they assumed everyone else already knew."
              </p>
              <p className="leading-[1.85] mb-6" style={{ color: "hsl(var(--ink-deep-foreground) / 0.75)" }}>
                That's why this site exists. Every article is edited personally,
                reviewed against authoritative sources, and written in the voice of
                a patient friend who happens to know this stuff. No upsells, no
                courses, no shame.
              </p>
              <Link
                to={`/author/${editor.slug}`}
                className="font-heading italic text-accent-warm hover:underline underline-offset-4"
              >
                — {editor.name}, {editor.role}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <EditorialDivider numeral="III" />

      {/* Why This Exists */}
      <section aria-label="About this site" className="py-20">
        <div className="container max-w-2xl">
          <p className="kicker-gold mb-4">§ III — Why this exists</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-10 text-balance">
            The stuff nobody teaches you
          </h2>
          <div className="drop-cap drop-cap-gold space-y-5 text-muted-foreground leading-[1.85]">
            <p>
              There's a moment most of us hit at some point—standing in a hardware store,
              staring at plumbing parts we can't name, wondering how everyone else seems to
              just... know this stuff.
            </p>
            <p>
              Or sitting at a kitchen table with a stack of paperwork, not sure what half
              of it means or what happens if we sign it wrong.
            </p>
            <p>
              The truth is, a lot of essential life skills simply aren't taught. Not in school.
              Not always at home. And by the time you're an adult, it feels embarrassing to admit
              you don't know how to read a contract, or what actually happens if you miss a bill
              payment, or how to cook something that isn't pasta.
            </p>
            <p>
              This site exists because we've been there. We've Googled "how to unblock drain" at
              midnight. We've pretended to understand our rental agreement. We've avoided phone
              calls because we didn't know what to say.
            </p>
            <p>
              Life Basics Guide is a collection of practical, no-judgment explanations for the
              things adults are somehow expected to figure out on their own. Each article is
              written like you're asking a patient friend who happens to know this stuff—someone
              who won't make you feel stupid for not knowing it already.
            </p>
            <p className="text-foreground font-medium font-heading italic text-lg">
              You're not behind. You're not broken. You just need information that was never
              handed to you—and that's exactly what we're here to provide.
            </p>
          </div>
        </div>
      </section>

      <EditorialDivider numeral="IV" />

      {/* This Week's Reading — numbered editorial list */}
      <section aria-label="This week's reading" className="py-16">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="kicker-gold mb-4">§ IV — This week's reading</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground leading-tight mb-4 text-balance">
                Eight pieces worth your evening.
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                The most-read articles right now — practical answers to the
                questions people search for most.
              </p>
            </div>

            <ol className="lg:col-span-8 divide-y divide-border stagger-children">
              {popularArticles.map((article, idx) => (
                <li key={article.slug} className="py-6 first:pt-0">
                  <Link
                    to={`/article/${article.slug}`}
                    className="group flex gap-6 items-start"
                  >
                    <span className="font-heading text-2xl md:text-3xl font-bold text-accent-warm tabular-figures flex-shrink-0 w-10 leading-none pt-1">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="label-editorial">{article.category}</span>
                      <h3 className="font-heading font-bold text-foreground text-lg mt-1 leading-snug group-hover:text-accent-warm transition-colors text-balance">
                        {article.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">
                        {article.description}
                      </p>
                    </div>
                    <div className="hidden md:block flex-shrink-0 w-28 h-20 rounded-sm img-hover-soft image-hairline">
                      <img
                        src={optimizedImage(article.featuredImage, 300) || article.featuredImage}
                        alt={article.featuredImageAlt || article.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                        width={300}
                        height={200}
                      />
                    </div>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <EditorialDivider numeral="V" />

      {/* Categories Grid — with subtle accent stripe per category */}
      <section aria-label="Browse by category" className="py-20 section-raised">
        <div className="container">
          <p className="kicker-gold justify-center mb-4" style={{ display: 'inline-flex' }}>§ V — Explore</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-12 text-center text-balance">
            Browse by category
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
            {categories.map((category) => {
              const Icon = category.icon;
              const linkedArticles = categoryArticleLinks[category.slug] || [];
              const accent = CATEGORY_ACCENTS[category.slug] || "38 45% 55%";
              return (
                <div
                  key={category.slug}
                  className="group p-6 border border-border rounded-sm card-editorial relative overflow-hidden bg-card"
                  style={{ borderTopWidth: "2px", borderTopColor: `hsl(${accent})` }}
                >
                  <Link to={`/category/${category.slug}`} className="block">
                    <Icon className="h-6 w-6 mb-4" style={{ color: `hsl(${accent})` }} />
                    <h3 className="font-heading font-bold text-foreground mb-2 group-hover:text-accent-warm transition-colors text-balance">
                      {category.name}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {category.description}
                    </p>
                  </Link>
                  {linkedArticles.length > 0 && (
                    <ul className="mt-5 space-y-2 pt-4 border-t border-border">
                      {linkedArticles.map(a => (
                        <li key={a.slug}>
                          <Link
                            to={`/article/${a.slug}`}
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors line-clamp-1"
                          >
                            → {a.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <EditorialDivider numeral="VI" />

      {/* What We Cover */}
      <section aria-label="What we cover" className="py-20">
        <div className="container max-w-3xl">
          <p className="kicker-gold justify-center mb-4" style={{ display: 'inline-flex' }}>§ VI — Eight essential areas</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-14 text-center text-balance">
            What we cover
          </h2>

          <div className="space-y-12 stagger-children">
            {categories.map((category) => {
              const Icon = category.icon;
              const accent = CATEGORY_ACCENTS[category.slug] || "38 45% 55%";
              return (
                <div key={category.slug} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <Icon className="h-5 w-5" style={{ color: `hsl(${accent})` }} />
                    </div>
                  </div>
                  <div>
                    <Link
                      to={`/category/${category.slug}`}
                      className="font-heading font-bold text-lg text-foreground link-underline"
                    >
                      {category.name}
                    </Link>
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      {category.longDescription}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <EditorialDivider numeral="VII" />

      {/* How to Use */}
      <section aria-label="How to use this site" className="py-20 section-sunken">
        <div className="container max-w-2xl">
          <p className="kicker-gold justify-center mb-4" style={{ display: 'inline-flex' }}>§ VII — Getting started</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-8 text-center text-balance">
            How to use this site
          </h2>
          <div className="space-y-5 text-muted-foreground leading-[1.85]">
            <p>
              This isn't a course. There's no start or finish. It's a reference—here when you need it.
            </p>
            <p>
              Browse by category if you want to explore. Use search if you have a specific question.
              Each article focuses on one topic and gives you steps you can follow immediately.
            </p>
            <p>
              The <Link to="/glossary" className="text-foreground link-underline">glossary</Link> explains
              common terms that often get thrown around without explanation. You'll find these linked
              throughout articles.
            </p>
            <p>
              Nothing here requires signup or payment. We built this because we needed it ourselves
              and figured others might too.
            </p>
            <p>
              Come back when something comes up. Bookmark articles that apply to your situation.
              Share them with someone who's asking the same questions.
            </p>
            <p className="text-foreground font-heading italic text-lg">
              That's it. No tricks. Just information.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
